import config from '@/config';
import type {
    ApiResponse,
    LoginCredentials,
    LoginResponse,
    RequestParams,
    PaginatedResponse,
    Player,
    Bet,
    AbnormalBet,
    WalletLog,
    SystemAlert,
    Vendor,
    DashboardStats, PaginatedData
} from '@/types';
// import {useToast} from "@/composables/useToast.ts";

/**
 * 管理后台API客户端
 * 用于处理与后端API的所有交互
 * 
 * 版本日志:
 * v2.0.0 - TypeScript版本
 * v1.0.6 - 2023-06-01: 使用统一配置文件和环境变量
 * v1.0.5 - 2023-05-25: 添加权限处理和401自动跳转到登录页
 * v1.0.4 - 2023-05-24: 添加登录和登出方法
 * v1.0.3 - 2023-05-23: 完善投注相关API方法
 * v1.0.2 - 2023-05-22: 添加getWalletStats方法
 * v1.0.1 - 2023-05-21: 统一所有API路由格式
 * v1.0.0 - 2023-05-20: 初始版本
 */
class AdminApiClient {
    private readonly _baseUrl: string;
    private readonly _timeout: number;
    private readonly _enableLogging: boolean;
    private readonly _headers: Record<string, string>;
    private readonly _version: string;

    get baseUrl(): string {
        return this._baseUrl;
    }

    get timeout(): number {
        return this._timeout;
    }

    get enableLogging(): boolean {
        return this._enableLogging;
    }

    get headers(): Record<string, string> {
        return this._headers;
    }

    get version(): string {
        return this._version;
    }

    /**
     * 构造函数
     */
    constructor() {
        // 从配置文件获取API基础URL
        this._baseUrl = config.apiBaseUrl + '/v1/admin/api';
        this._timeout = config.requestTimeout;
        this._enableLogging = config.enableLogging;
        
        this._headers = {
            'Content-Type': 'application/json'
        };
        
        // 记录客户端信息
        this._version = '2.0.0';
        
        // 输出初始化信息
        if (this._enableLogging) {
            console.log(`%c[AdminApiClient v${this._version}] 初始化成功 (${this._baseUrl})`, 'background: #333; color: #bada55; padding: 2px 4px; border-radius: 2px;');
        }
        
        // 设置全局版本信息，便于调试
        (window as any).apiClientVersion = this._version;
        
        // 检查并设置认证token
        this.initTokenFromStorage();
    }

    /**
     * 从localStorage初始化token
     */
    private initTokenFromStorage(): void {
        const token = localStorage.getItem('admin_token');
        if (token) {
            this._headers['Authorization'] = `Bearer ${token}`;
        }
    }

    /**
     * 处理认证错误（401）
     * 清除认证信息并跳转到登录页面
     */
    private handleAuthError(): void {
        // 清除认证信息
        this.clearAuth();
        
        // 保存当前URL，以便登录后可以返回
        const currentPath = window.location.pathname + window.location.search;
        localStorage.setItem('login_redirect', currentPath);
        
        // // 跳转到登录页面
        // window.location.href = '/admin';
    }

    /**
     * 执行GET请求
     */
    async get<T = any>(endpoint: string, params: RequestParams = {}): Promise<ApiResponse<T>> {
        const url = new URL(this._baseUrl + endpoint, window.location.origin);
        
        // 添加URL参数
        Object.keys(params).forEach(key => {
            const value = params[key];
            if (value !== null && value !== undefined && value !== '') {
                url.searchParams.append(key, String(value));
            }
        });
        
        // 确保Authorization header是最新的
        this.initTokenFromStorage();
        
        try {
            // 创建一个AbortController，用于设置请求超时
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), this._timeout);
            
            if (this._enableLogging) {
                console.log(`[API GET] ${url}`);
            }
            
            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: this._headers,
                credentials: 'same-origin',
                signal: controller.signal
            });
            
            // 清除超时
            clearTimeout(timeoutId);
            
            // 解析响应体
            const result: ApiResponse<T> = await response.json();
            
            // 检查特定错误码
            if (result && result.status === 1036) {
                // 导入 useToast (确保在组件中使用时可用)
                try {
                    const { useToast } = await import('@/composables/useToast');
                    const { showToast } = useToast();
                    // 显示错误提示
                    showToast({
                        message: result.message || '必填字段缺失，请检查输入',
                        type: 'error'
                    });
                } catch (err) {
                    // 如果无法导入 useToast，降级到 console.error
                    console.error('字段验证错误:', result.message);
                }
            }
            
            if (!response.ok) {
                if (response.status === 401) {
                    // 未登录，跳转到登录页面
                    const error = new Error('用户未登录或登录已过期');
                    (error as any).status = 401;
                    this.handleAuthError();
                    throw error;
                } else if (response.status === 403) {
                    // 无权限
                    const error = new Error('您没有访问该资源的权限');
                    (error as any).status = 403;
                    throw error;
                } else {
                    throw new Error(`HTTP错误! 状态: ${response.status}`);
                }
            }
            
            return result;
        } catch (error: any) {
            if (error.name === 'AbortError') {
                console.error(`API请求超时: ${url}`);
                throw new Error('请求超时，请稍后重试');
            }
            console.error('API请求失败:', error);
            throw error;
        }
    }

    /**
     * 执行POST请求
     */
    async post<T = any>(endpoint: string, data: any = {}): Promise<ApiResponse<T>> {
        const url = new URL(this._baseUrl + endpoint, window.location.origin);
        
        // 确保Authorization header是最新的
        this.initTokenFromStorage();
        
        try {
            // 创建一个AbortController，用于设置请求超时
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), this._timeout);
            
            if (this._enableLogging) {
                console.log(`[API POST] ${url}`, data);
            }
            
            const response = await fetch(url.toString(), {
                method: 'POST',
                headers: this._headers,
                credentials: 'same-origin',
                body: JSON.stringify(data),
                signal: controller.signal
            });
            
            // 清除超时
            clearTimeout(timeoutId);
            
            // 解析响应体
            const result: ApiResponse<T> = await response.json();
            
            // 检查特定错误码
            if (result && result.status === 1036) {
                // 导入 useToast (确保在组件中使用时可用)
                try {
                    const { useToast } = await import('@/composables/useToast');
                    const { showToast } = useToast();
                    // 显示错误提示
                    showToast({
                        message: result.message || '必填字段缺失，请检查输入',
                        type: 'error'
                    });
                } catch (err) {
                    // 如果无法导入 useToast，降级到 console.error
                    console.error('字段验证错误:', result.message);
                }
            }

            if (!response.ok) {
                if (response.status === 401) {
                    // 未登录，跳转到登录页面
                    const error = new Error(result.message ?? '用户未登录或登录已过期');
                    (error as any).status = 401;
                    this.handleAuthError();
                    throw error;
                } else if (response.status === 403) {
                    // 无权限
                    const error = new Error(result.message ??'您没有执行该操作的权限');
                    (error as any).status = 403;
                    throw error;
                } else {
                    throw new Error(`HTTP错误! 状态: ${response.status}`);
                }
            }
            
            return result;
        } catch (error: any) {
            if (error.name === 'AbortError') {
                console.error(`API请求超时: ${url}`);
                throw new Error('请求超时，请稍后重试');
            }
            console.error('API请求失败:', error);
            throw error;
        }
    }

    // ============= 认证相关 =============
    
    /**
     * 管理员登录
     */
    async login(credentials: LoginCredentials): Promise<ApiResponse<LoginResponse>> {
        const response = await this.post<LoginResponse>('/login', credentials);
        if (response.success && response.data && response.data.token) {
            // 保存token到localStorage
            localStorage.setItem('admin_token', response.data.token);
            
            // 保存用户信息
            if (response.data.user) {
                localStorage.setItem('admin_user', JSON.stringify(response.data.user));
            }
            
            // 设置请求头
            this._headers['Authorization'] = `Bearer ${response.data.token}`;
        }
        
        return response;
    }
    
    /**
     * 检查认证状态
     */
    async checkAuthStatus(): Promise<ApiResponse> {
        return await this.get('/check');
    }
    
    /**
     * 管理员登出
     */
    async logout(): Promise<ApiResponse> {
        try {
            // 调用后端登出API
            const response = await this.post('/auth/logout');
            
            // 无论成功与否，清除前端存储
            this.clearAuth();
            
            return response;
        } catch (error) {
            // 即使API调用失败，也要清除前端登录信息
            this.clearAuth();
            throw error;
        }
    }
    
    /**
     * 清除认证信息
     */
    clearAuth(): void {
        // 移除token
        localStorage.removeItem('admin_token');
        
        // 移除用户信息
        localStorage.removeItem('admin_user');
        
        // 移除请求头中的Authorization
        delete this._headers['Authorization'];
    }

    /**
     * 检查是否已登录
     */
    isLoggedIn(): boolean {
        return !!localStorage.getItem('admin_token');
    }
    
    /**
     * 获取当前登录用户信息
     */
    getCurrentUser(): any {
        const userJson = localStorage.getItem('admin_user');
        if (userJson) {
            try {
                return JSON.parse(userJson);
            } catch (e) {
                console.error('解析用户信息失败:', e);
                return null;
            }
        }
        return null;
    }

    // ============= 测试相关 =============
    
    /**
     * API测试
     */
    testApi(): Promise<ApiResponse> {
        return this.get('/test');
    }
    
    /**
     * CSRF测试
     */
    testCsrf(): Promise<ApiResponse> {
        return this.post('/test-csrf');
    }

    // ============= Dashboard相关 =============
    
    /**
     * 获取仪表盘数据
     */
    getDashboardData(params: RequestParams = {}): Promise<ApiResponse<DashboardStats>> {
        return this.get<DashboardStats>('/dashboard/data', params);
    }

    // ============= 玩家相关 =============
    
    /**
     * 获取玩家列表
     */
    getPlayers(filters: RequestParams = {}, page: number = 1, perPage: number = 15): Promise<PaginatedResponse<Player[]>> {
        return this.get<PaginatedData<Player[]>>('/players', {
            ...filters,
            page,
            per_page: perPage
        }) as Promise<PaginatedResponse<Player[]>>;
    }

    /**
     * 获取玩家统计数据
     */
    getPlayerStats(): Promise<ApiResponse> {
        return this.get('/players/stats');
    }

    /**
     * 获取玩家详情
     */
    getPlayerDetails(playerId: string | number): Promise<ApiResponse<Player>> {
        return this.get<Player>(`/players/${playerId}`);
    }

    /**
     * 更新玩家状态
     */
    updatePlayerStatus(playerId: string | number, status: number): Promise<ApiResponse> {
        return this.post(`/players/${playerId}/status`, { status });
    }

    // ============= 钱包相关 =============
    
    /**
     * 获取钱包统计数据
     */
    getWalletStats(): Promise<ApiResponse> {
        console.log('[AdminApiClient] 调用getWalletStats()');
        return this.get('/wallet/stats');
    }
    
    /**
     * 获取钱包日志
     */
    getWalletLogs(filters: RequestParams = {}, page: number = 1, perPage: number = 15): Promise<PaginatedResponse<WalletLog[]>> {
        return this.get<PaginatedData<WalletLog[]>>('/wallet/logs', {
            ...filters,
            page,
            per_page: perPage
        }) as Promise<PaginatedResponse<WalletLog[]>>;
    }

    // ============= 投注相关 =============
    
    /**
     * 获取投注列表
     */
    getBets(params: RequestParams): Promise<PaginatedResponse<Bet[]>> {
        return this.get<PaginatedData<Bet[]>>('/bets', params) as Promise<PaginatedResponse<Bet[]>>;
    }

    /**
     * 获取注单比较选项（厂商和币种列表）
     */
    getCompareOptions(): Promise<ApiResponse<{ vendors: Vendor[], currencies: any[] }>> {
        return this.get<{ vendors: Vendor[], currencies: any[] }>('/bets/compare/options');
    }
    
    /**
     * 获取投注统计信息
     */
    getBetsStats(): Promise<ApiResponse> {
        return this.get('/bets/stats');
    }
    
    /**
     * 获取指定投注详情
     */
    getBetDetails(params: RequestParams = {}): Promise<ApiResponse<Bet>> {
        return this.get<Bet>(`/bets/details`, params);
    }
    
    /**
     * 获取游戏记录
     */
    getGameRecords(fundsn: string, system: string): Promise<ApiResponse> {
        return this.get(`/bets/game-records/${fundsn}/${system}`);
    }
    
    /**
     * 获取投注修改历史
     */
    getBetEditHistory(params: RequestParams = {}): Promise<ApiResponse> {
        return this.get('/bets/edit-history', params);
    }
    
    /**
     * 更新投注信息
     */
    updateBet(data: any): Promise<ApiResponse> {
        return this.post('/bets/update', data);
    }
    
    /**
     * 回滚投注编辑
     */
    rollbackBetEdit(data: any): Promise<ApiResponse> {
        return this.post('/bets/rollback-edit', data);
    }
    
    /**
     * 获取投注导出链接
     */
    getBetsExportUrl(filters: RequestParams = {}): string {
        const url = new URL(`${this._baseUrl}/bets/export`, window.location.origin);
        
        // 添加URL参数
        Object.keys(filters).forEach(key => {
            const value = filters[key];
            if (value !== null && value !== undefined && value !== '') {
                url.searchParams.append(key, String(value));
            }
        });
        
        return url.toString();
    }
    
    /**
     * 获取异常投注列表
     */
    getAbnormalBets(params: RequestParams): Promise<PaginatedResponse<AbnormalBet[]>> {
        return this.get<PaginatedData<AbnormalBet[]>>('/bets/abnormal', params) as Promise<PaginatedResponse<AbnormalBet[]>>;
    }
    
    /**
     * 标记异常投注
     */
    markAbnormalBet(data: any): Promise<ApiResponse> {
        return this.post('/bets/abnormal/mark', data);
    }
    
    /**
     * 解决异常投注
     */
    async resolveAbnormalBet(data: any): Promise<ApiResponse> {
        return this.post('/bets/abnormal/resolve', data);
    }
    
    /**
     * 获取报表数据
     */
    getReportsData(params: RequestParams = {}): Promise<ApiResponse> {
        return this.get('/reports/data', params);
    }
    
    // ============= 系统相关 =============
    
    /**
     * 获取系统性能数据
     */
    getSystemPerformance(params: RequestParams = {}): Promise<ApiResponse> {
        return this.get('/system/performance', params);
    }
    
    /**
     * 获取系统告警列表
     */
    // Vue: Conversion of type Promise<ApiResponse<SystemAlert[]>> to type Promise<PaginatedResponse<SystemAlert[]>> may be a mistake because neither type sufficiently overlaps with the other. If this was intentional, convert the expression to unknown first.
    // Type ApiResponse<SystemAlert[]> is not comparable to type PaginatedResponse<SystemAlert[]>
    getSystemAlerts(params: RequestParams = {}): Promise<PaginatedResponse<SystemAlert>> {
        return this.get<PaginatedData<SystemAlert>>('/system/alerts', params);
    }
    
    /**
     * 获取系统日志列表
     */
    getSystemLogs(params: RequestParams = {}): Promise<ApiResponse> {
        return this.get('/system/logs', params);
    }
    
    /**
     * 获取系统报警列表
     */
    getSystemAlarms(): Promise<ApiResponse> {
        return this.get('/system/alarms');
    }
    
    /**
     * 检查系统性能
     */
    checkSystemPerformance(): Promise<ApiResponse> {
        console.log('[AdminApiClient] 调用checkSystemPerformance()');
        // 这里是一个POST请求，因为它会触发服务器端执行检查操作
        return this.post('/system/check-performance');
    }
    
    /**
     * 获取厂商回调列表
     */
    getVendorCallbacks(params: RequestParams = {}): Promise<ApiResponse> {
        return this.get('/system/vendor-callback/list', params);
    }
    
    /**
     * 获取游戏厂商列表
     */
    getVendors(): Promise<ApiResponse<Vendor[]>> {
        return this.get<Vendor[]>('/system/vendor-callback/vendors');
    }
    
    /**
     * 获取回调详情
     */
    getCallbackDetail(params: RequestParams = {}): Promise<ApiResponse> {
        return this.get('/system/vendor-callback/detail', params);
    }
    
    /**
     * 重新发送回调
     */
    resendCallback(data: any): Promise<ApiResponse> {
        return this.post('/system/vendor-callback/resend', data);
    }
    
    /**
     * 搜索日志
     */
    searchLogs(params: RequestParams = {}): Promise<ApiResponse> {
        return this.get('/system/search-logs', params);
    }
    
    /**
     * 查看日志内容
     */
    viewLogContent(params: RequestParams = {}): Promise<ApiResponse> {
        return this.get('/system/view-log', params);
    }
    
    /**
     * 获取日志下载链接
     */
    getLogDownloadUrl(params: RequestParams = {}): string {
        const url = new URL(`${this._baseUrl}/system/download-log`, window.location.origin);
        
        // 添加URL参数
        Object.keys(params).forEach(key => {
            const value = params[key];
            if (value !== null && value !== undefined && value !== '') {
                url.searchParams.append(key, String(value));
            }
        });
        
        return url.toString();
    }
    
    /**
     * 获取所有日志的下载链接
     */
    getAllLogsDownloadUrl(params: RequestParams = {}): string {
        const url = new URL(`${this._baseUrl}/system/download-all-logs`, window.location.origin);
        
        // 添加URL参数
        Object.keys(params).forEach(key => {
            const value = params[key];
            if (value !== null && value !== undefined && value !== '') {
                url.searchParams.append(key, String(value));
            }
        });
        
        return url.toString();
    }
    
    /**
     * 删除日志文件
     */
    deleteLog(data: any): Promise<ApiResponse> {
        return this.post('/system/delete-log', data);
    }

    /**
     * 获取注单对比数据
     */
    async getBetComparison(params: RequestParams = {}): Promise<ApiResponse> {
        return await this.post('/bets/compare/run', params);
    }

    /**
     * 修复不匹配的注单
     */
    async fixBetMismatch(data: { bet_id: string; fix_type: string }): Promise<ApiResponse> {
        return await this.post('/bets/comparison/fix', data);
    }

    /**
     * 获取游戏分类选项
     */
    async getGameCategories(): Promise<ApiResponse> {
        return await this.get('/game/categories');
    }
}

// Export the API client
export default AdminApiClient;
